import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Eye,
  Heart,
  ChatCircle,
  ArrowClockwise,
  ShieldCheck,
  Target,
  Wallet,
} from '@phosphor-icons/react';

import { Button } from '../components/ui/Button';
import { HeroDeviceMock } from '../components/landing/HeroDeviceMock';
import { LandingHighlights } from '../components/landing/LandingHighlights';
import { LandingRiskLine } from '../components/landing/LandingRiskLine';
import { EvolutionChart } from '../components/landing/EvolutionChart';
import { StepCard } from '../components/landing/StepCard';
import { FeatureRow } from '../components/landing/FeatureRow';
import { BudgetMiniApp } from '../components/landing/BudgetMiniApp';
import { GoalMiniApp } from '../components/landing/GoalMiniApp';
import { EmergencyFundMiniApp } from '../components/landing/EmergencyFundMiniApp';
import { FearCard } from '../components/landing/FearCard';
import { FinalCta } from '../components/landing/FinalCta';
import {
  ScrollReveal,
  StaggerGroup,
  StaggerItem,
} from '../components/landing/ScrollReveal';
import pt from '../locales/pt';

const FEATURE_ICONS = [Wallet, Target, ShieldCheck];
const FEATURE_ART = [
  <BudgetMiniApp key="budget" />,
  <GoalMiniApp key="goal" />,
  <EmergencyFundMiniApp key="fund" />,
];
const FEAR_ICONS = [Heart, ChatCircle, Eye];

const sectionPad =
  'px-space-5 py-space-16 mid:px-space-10 mid:py-space-16 wide:px-space-20 wide:py-space-24';
const sectionInner = 'mx-auto w-full max-w-[1120px]';

/**
 * Marketing landing — Landing · 375/768/1280 × light/dark in
 * evplanner-gleb-design-system.pen + scroll storytelling (Framer).
 */
export function Landing() {
  return (
    <div className="flex flex-col">
      {/* Hero — motion: fade-up on mount · copy stagger 0.08 · phone delay 0.24 */}
      <section className="px-space-5 pb-space-16 pt-space-12 mid:px-space-10 mid:pb-space-16 mid:pt-space-12 wide:px-space-20 wide:pb-space-24 wide:pt-space-16">
        <div
          className={`${sectionInner} grid items-center gap-space-12 text-center mid:grid-cols-2 mid:gap-space-16 mid:text-left`}
        >
          <StaggerGroup
            className="flex flex-col items-center gap-space-6 mid:items-start"
            stagger={0.08}
            onMount
          >
            <StaggerItem>
              <span className="inline-flex items-center gap-space-2 rounded-ds-full border border-border bg-sage-50 px-space-3 py-space-2 type-body-sm font-semibold text-sage-800 dark:bg-sage-900/40 dark:text-sage-200">
                <ArrowClockwise className="h-3.5 w-3.5" aria-hidden />
                {pt.landing.hero.badge}
              </span>
            </StaggerItem>
            <StaggerItem>
              <h1 className="type-landing-hero max-w-[18ch] text-balance text-foreground mid:max-w-none">
                {pt.landing.hero.title}
              </h1>
            </StaggerItem>
            <StaggerItem>
              <p className="type-landing-lead max-w-[460px] text-pretty">
                {pt.landing.hero.subtitle}
              </p>
            </StaggerItem>
            <StaggerItem className="flex w-full flex-col items-center gap-space-3 mid:items-start">
              <Link to="/sign-up" className="w-full mid:w-auto">
                <Button
                  size="lg"
                  className="h-14 w-full gap-space-2 rounded-ds-full px-space-8 text-type-display-stat shadow-[0_16px_30px_-14px_rgba(95,126,84,0.6)] mid:w-auto"
                >
                  {pt.landing.hero.cta}
                  <ArrowRight className="h-5 w-5" aria-hidden />
                </Button>
              </Link>
              <LandingRiskLine className="justify-center mid:justify-start" />
            </StaggerItem>
          </StaggerGroup>
          <HeroDeviceMock />
        </div>
      </section>

      <LandingHighlights />

      {/* Como funciona */}
      <section className={`bg-background ${sectionPad}`}>
        <div className={`${sectionInner} text-center`}>
          <ScrollReveal className="flex flex-col items-center gap-space-3">
            <span className="type-section text-sage-700 dark:text-sage-300">
              {pt.landing.how.tag}
            </span>
            <h2 className="type-landing-h2">{pt.landing.how.title}</h2>
          </ScrollReveal>
          <StaggerGroup
            className="mt-space-10 flex flex-col gap-space-8 text-left mid:mt-space-12 mid:flex-row mid:gap-space-12"
            stagger={0.12}
          >
            {pt.landing.how.steps.map((step, index) => (
              <StepCard
                key={step.title}
                number={index + 1}
                title={step.title}
                description={step.description}
              />
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Selfie / concept + bar chart */}
      <section className={`bg-muted ${sectionPad}`}>
        <div
          className={`${sectionInner} grid items-center gap-space-10 mid:grid-cols-2 mid:gap-space-16`}
        >
          <ScrollReveal className="flex flex-col gap-space-4">
            <span className="type-section text-sage-700 dark:text-sage-300">
              {pt.landing.concept.tag}
            </span>
            <h2 className="type-landing-h2">{pt.landing.concept.title}</h2>
            <p className="type-landing-lead max-w-prose">{pt.landing.concept.lead}</p>
            <ul className="mt-space-2 flex flex-col gap-space-2">
              {pt.landing.concept.bullets.map((bullet) => (
                <li
                  key={bullet}
                  className="flex items-center gap-space-2 type-body font-medium text-foreground"
                >
                  <span className="h-1.5 w-1.5 rounded-ds-full bg-sage-600" aria-hidden />
                  {bullet}
                </li>
              ))}
            </ul>
          </ScrollReveal>
          <ScrollReveal y={32} delay={0.1}>
            <EvolutionChart />
          </ScrollReveal>
        </div>
      </section>

      {/* Features */}
      <section className={`bg-background ${sectionPad}`}>
        <div className={`${sectionInner} mb-space-10 text-center mid:mb-space-16`}>
          <ScrollReveal className="flex flex-col items-center gap-space-3">
            <span className="type-section text-sage-700 dark:text-sage-300">
              {pt.landing.features.tag}
            </span>
            <h2 className="type-landing-h2 max-w-[20ch]">{pt.landing.features.title}</h2>
          </ScrollReveal>
        </div>
        <div className={`${sectionInner} flex flex-col gap-space-16 mid:gap-space-24`}>
          {pt.landing.features.items.map((item, index) => (
            <FeatureRow
              key={item.title}
              icon={FEATURE_ICONS[index]}
              title={item.title}
              description={item.description}
              art={FEATURE_ART[index]}
              reverse={index % 2 === 1}
            />
          ))}
        </div>
      </section>

      {/* Sem medo */}
      <section className="bg-sage-700 py-space-16 mid:py-space-16 wide:py-space-24">
        <div className={`${sectionInner} px-space-5 text-center mid:px-space-10 wide:px-space-20`}>
          <ScrollReveal className="flex flex-col items-center gap-space-3">
            <span className="type-section text-sage-200">{pt.landing.fear.tag}</span>
            <h2 className="type-landing-h2 text-sage-50">{pt.landing.fear.title}</h2>
          </ScrollReveal>
          <StaggerGroup
            className="mt-space-10 grid gap-space-6 text-left mid:mt-space-12 mid:grid-cols-3"
            stagger={0.1}
          >
            {pt.landing.fear.cards.map((card, index) => {
              const Icon = FEAR_ICONS[index];
              return (
                <StaggerItem key={card.title}>
                  <FearCard
                    icon={<Icon className="h-5 w-5" aria-hidden />}
                    title={card.title}
                    description={card.description}
                  />
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      <FinalCta />
    </div>
  );
}
